// Robust Monarch syntax highlighting definition and language configuration for XML in Monaco Editor

export const xmlLanguageConfig = {
  comments: {
    blockComment: ["<!--", "-->"],
  },
  brackets: [["<", ">"]],
  autoClosingPairs: [
    { open: "<", close: ">" },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  surroundingPairs: [
    { open: "<", close: ">" },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  folding: {
    markers: {
      start: new RegExp("^\\s*<!--\\s*#region\\b.*-->"),
      end: new RegExp("^\\s*<!--\\s*#endregion\\b.*-->"),
    },
  },
};

export const xmlMonarchTokens = {
  defaultToken: "",
  tokenPostfix: ".xml",
  ignoreCase: false,

  tokenizer: {
    root: [
      [/[^<&]+/, ""],
      { include: "@whitespace" },

      // Comments: <!-- ... -->
      [/<!--/, { token: "comment", next: "@comment" }],

      // CDATA: <![CDATA[ ... ]]>
      [/<!\[CDATA\[/, { token: "metatag", next: "@cdata" }],

      // DOCTYPE: <!DOCTYPE ... >
      [/<!DOCTYPE/, { token: "metatag", next: "@doctype" }],

      // XML Declaration / Processing Instructions: <?xml ... ?>
      [/<\?[a-zA-Z_][\w.-]*/, { token: "metatag", next: "@pi" }],

      // Closing tags: </tagName> (handles namespaces like mml:math)
      [/<\/([a-zA-Z_][\w.-]*(?::[a-zA-Z_][\w.-]*)?)/, { token: "tag", next: "@tagEnd" }],

      // Opening or empty tags: <tagName ...>
      [/<([a-zA-Z_][\w.-]*(?::[a-zA-Z_][\w.-]*)?)/, { token: "tag", next: "@tag" }],

      // XML Entities: &amp;, &#123;, &#xabc;
      [/&(?:[a-zA-Z_][\w.-]*|#\d+|#x[a-fA-F0-9]+);/, "entity"],
    ],

    tag: [
      { include: "@whitespace" },

      // Attribute names (handles namespaces like xmlns:xlink, xlink:href, contrib-type, etc.)
      [/([a-zA-Z_][\w.-]*(?::[a-zA-Z_][\w.-]*)?)/, "attribute.name"],

      // Equals delimiter
      [/=/, "delimiter"],

      // Double-quoted attribute value
      [/"([^"]*)"/, "attribute.value"],

      // Single-quoted attribute value
      [/'([^']*)'/, "attribute.value"],

      // Self-closing tag end: />
      [/\/>/, { token: "tag", next: "@pop" }],

      // Standard tag end: >
      [/>/, { token: "tag", next: "@pop" }],

      // Fallback inside tag to avoid getting stuck
      [/[^ \t\r\n=>"']+/, "attribute.name"],
    ],

    tagEnd: [
      { include: "@whitespace" },
      [/[^>]+/, "tag"],
      [/>/, { token: "tag", next: "@pop" }],
    ],

    pi: [
      [/\?>/, { token: "metatag", next: "@pop" }],
      { include: "@whitespace" },
      [/([a-zA-Z_][\w.-]*(?::[a-zA-Z_][\w.-]*)?)/, "attribute.name"],
      [/=/, "delimiter"],
      [/"([^"]*)"/, "attribute.value"],
      [/'([^']*)'/, "attribute.value"],
      [/./, "metatag"],
    ],

    comment: [
      [/-->/, { token: "comment", next: "@pop" }],
      [/[^-]+/, "comment"],
      [/./, "comment"],
    ],

    cdata: [
      [/\]\]>/, { token: "metatag", next: "@pop" }],
      [/[^\]]+/, "string"],
      [/./, "string"],
    ],

    doctype: [
      [/>/, { token: "metatag", next: "@pop" }],
      { include: "@whitespace" },
      [/"([^"]*)"/, "attribute.value"],
      [/'([^']*)'/, "attribute.value"],
      [/./, "metatag"],
    ],

    whitespace: [[/[ \t\r\n]+/, "white"]],
  },
};

export const registerXmlMonacoLanguage = (monaco) => {
  if (!monaco) return;

  try {
    // Register language if not already registered
    const languages = monaco.languages.getLanguages() || [];
    const hasXml = languages.some((l) => l.id === "xml");
    if (!hasXml) {
      monaco.languages.register({ id: "xml" });
    }

    // Set Monarch tokens provider and language configuration
    monaco.languages.setMonarchTokensProvider("xml", xmlMonarchTokens);
    monaco.languages.setLanguageConfiguration("xml", xmlLanguageConfig);

    // Define crisp, rich XML theme
    monaco.editor.defineTheme("vedasXmlTheme", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "tag", foreground: "0000FF", fontStyle: "bold" },
        { token: "attribute.name", foreground: "9A0505", fontStyle: "normal" },
        { token: "attribute.value", foreground: "0451A5" },
        { token: "delimiter", foreground: "666666" },
        { token: "comment", foreground: "008000", fontStyle: "italic" },
        { token: "metatag", foreground: "800080", fontStyle: "bold" },
        { token: "entity", foreground: "D97706" },
        { token: "string", foreground: "0451A5" },
      ],
      colors: {
        "editor.background": "#FFFFFF",
        "editorLineNumber.foreground": "#94A3B8",
        "editorLineNumber.activeForeground": "#4F46E5",
      },
    });

    monaco.editor.setTheme("vedasXmlTheme");
  } catch (err) {
    console.warn("Error setting up Monaco XML language:", err);
  }
};
